import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Highlighter, Loader2, Quote } from 'lucide-react';
import {
  FilePreviewOverlay,
  type FilePreviewSource,
} from '@/components/documents/FilePreviewOverlay';
import { Button } from '@/components/ui/Button';
import { HighlightTagPills } from '@/components/documents/HighlightTagFace';
import { JournalMarkingTagFilter } from '@/components/journal/JournalMarkingTagFilter';
import { usePatientFileHighlights, type PatientFileHighlight } from '@/hooks/useDocumentHighlights';
import { useDocumentHighlightTags } from '@/hooks/useDocumentHighlightTags';
import { journalQuoteHtml } from '@/lib/journalQuote';
import {
  documentCitationAttribution,
  documentCitationAuthor,
  documentCitationName,
  formatDocumentCitationDate,
} from '@/lib/markingCitation';
import {
  filterJournalWriteMarkings,
  journalMarkingFilterTags,
  journalMarkingTagCounts,
  markingQuotePreview,
} from '@/lib/journalWriteMarkings';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { formatHighlightTime, highlightColorCssVar } from '@/utils/fileHighlights';

const ICON = 1.75;

/**
 * File markings inside Tidligere notater og filer.
 * Same rows clinicians save in the file preview (`document_highlights`).
 */
export function JournalWritePatientMarkings({
  clinicId,
  patientId,
  practitionerId,
  practitionerMemberIds,
  activeMemberIds,
  search,
  active = true,
  onVisibleCount,
  onCite,
}: {
  clinicId: string;
  patientId: string;
  practitionerId: string;
  practitionerMemberIds?: readonly string[];
  activeMemberIds?: readonly string[];
  search: string;
  active?: boolean;
  onVisibleCount?: (count: number | null) => void;
  /**
   * Same insert path as Notater → Citér markeret. Omit outside journal
   * writing (the fullscreen copy has no editor insert).
   */
  onCite?: (html: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const highlights = usePatientFileHighlights(active ? clinicId : null, active ? patientId : null);
  const highlightTags = useDocumentHighlightTags(active ? clinicId : null);
  const [preview, setPreview] = useState<FilePreviewSource | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const onVisibleCountRef = useRef(onVisibleCount);
  onVisibleCountRef.current = onVisibleCount;

  const rows = useMemo(() => highlights.data ?? [], [highlights.data]);
  const practitionerIds = useMemo(
    () => new Set(practitionerMemberIds ?? []),
    [practitionerMemberIds],
  );
  const activeIds = useMemo(() => new Set(activeMemberIds ?? []), [activeMemberIds]);
  const staffRows = useMemo(
    () =>
      filterJournalWriteMarkings(rows, {
        practitionerId,
        practitionerIds,
        activeMemberIds: activeIds,
        search: '',
      }),
    [activeIds, practitionerId, practitionerIds, rows],
  );
  const tagOptions = useMemo(() => {
    const used = new Set<string>();
    for (const row of staffRows) {
      for (const id of row.tagIds ?? []) used.add(id);
    }
    return journalMarkingFilterTags(highlightTags.data ?? [], used);
  }, [highlightTags.data, staffRows]);
  const optionIds = useMemo(() => new Set(tagOptions.map((tag) => tag.id)), [tagOptions]);
  const appliedTagIds = useMemo(
    () => selectedTagIds.filter((id) => optionIds.has(id)),
    [optionIds, selectedTagIds],
  );
  const countedRows = useMemo(
    () =>
      filterJournalWriteMarkings(rows, {
        practitionerId,
        practitionerIds,
        activeMemberIds: activeIds,
        search,
      }),
    [activeIds, practitionerId, practitionerIds, rows, search],
  );
  const tagCounts = useMemo(() => journalMarkingTagCounts(countedRows), [countedRows]);
  const visible = useMemo(
    () =>
      filterJournalWriteMarkings(rows, {
        practitionerId,
        practitionerIds,
        activeMemberIds: activeIds,
        search,
        tagIds: appliedTagIds,
      }),
    [activeIds, appliedTagIds, practitionerId, practitionerIds, rows, search],
  );

  useEffect(() => {
    setSelectedTagIds([]);
  }, [clinicId]);

  useEffect(() => {
    if (!active) return;
    if (highlights.isLoading || highlights.isError) {
      onVisibleCountRef.current?.(null);
      return;
    }
    onVisibleCountRef.current?.(visible.length);
  }, [active, highlights.isError, highlights.isLoading, visible.length]);

  const openPreview = (row: PatientFileHighlight) => {
    setPreview({
      type: 'document',
      documentId: row.document_id,
      filename: row.filename,
      mimeType: row.mime_type,
      highlightId: row.id,
    });
  };

  const citeMarking = (row: PatientFileHighlight) => {
    if (!onCite) return;
    const text = row.quote.trim();
    if (!text) return;
    const file = documentCitationName({
      documentTitle: row.document_title,
      filename: row.filename,
    });
    const html = journalQuoteHtml({
      text,
      attribution: documentCitationAttribution({
        fileLabel: file ? t('journal.quote_attribution_marking_file', { file }) : '',
        pageLabel: t('journal.quote_attribution_marking_page', { page: row.page }),
        dateLabel: formatDocumentCitationDate(row.document_date, i18n.language),
        author: documentCitationAuthor(row.document_author),
      }),
    });
    if (html) onCite(html);
  };

  return (
    <>
      {preview ? <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} /> : null}
      <div className="jw-files-body">
        {highlights.isLoading ? (
          <div className="jw-state" role="status" aria-live="polite">
            <Loader2 className="h-6 w-6 animate-spin" strokeWidth={ICON} aria-hidden />
            {t('app.loading')}
          </div>
        ) : highlights.isError ? (
          <div className="jw-state" role="alert">
            <p>{t('documents.highlights.loadError')}</p>
            <Button type="button" variant="secondary" onClick={() => void highlights.refetch()}>
              {t('app.retry')}
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="jw-state">
            <Highlighter className="h-7 w-7" strokeWidth={ICON} aria-hidden />
            <p>{t('journal.history_markings_empty')}</p>
            <p className="text-sm text-ds-muted">{t('journal.history_markings_empty_hint')}</p>
          </div>
        ) : (
          <>
            {tagOptions.length > 0 ? (
              <div className="jw-file-tools">
                <JournalMarkingTagFilter
                  tags={tagOptions}
                  counts={tagCounts}
                  total={countedRows.length}
                  selectedIds={appliedTagIds}
                  onToggle={(id) => {
                    setSelectedTagIds((current) => {
                      const kept = current.filter((item) => optionIds.has(item));
                      return kept.includes(id) ? kept.filter((item) => item !== id) : [...kept, id];
                    });
                  }}
                  onClear={() => setSelectedTagIds([])}
                />
              </div>
            ) : null}
            {visible.length === 0 ? (
              <div className="jw-state">
                <p>{t('journal.history_markings_none')}</p>
              </div>
            ) : (
              <ul className="jw-list jw-scroller" aria-label={t('journal.history_tab_markings')}>
                {visible.map((row) => {
                  const author = row.author_name || t('documents.highlights.unknown_author');
                  const when = formatHighlightTime(row.created_at, i18n.language);
                  const quote = row.quote.trim();
                  const canCite = Boolean(onCite) && quote.length > 0;
                  return (
                    <li key={row.id} className="jw-it">
                      <button
                        type="button"
                        className={cn('jw-mark-open', canCite && 'has-cite')}
                        data-testid={`journal-history-marking-${row.id}`}
                        onClick={() => openPreview(row)}
                      >
                        <span className="jw-mark-top">
                          <span
                            className="jw-mark-swatch"
                            style={{ ['--mark' as string]: highlightColorCssVar(row.color) }}
                            aria-hidden
                          />
                          <span className="jw-mark-quote">{markingQuotePreview(row.quote)}</span>
                        </span>
                        <HighlightTagPills tags={highlightTags.data ?? []} tagIds={row.tagIds} />
                        {row.comment ? (
                          <span className={cn('jw-mark-comment', TYPE.small, 'text-ds-muted')}>
                            {row.comment}
                          </span>
                        ) : null}
                        <span className={cn(TYPE.small, 'text-ds-muted')}>
                          {[author, when].filter(Boolean).join(' · ')}
                          {' · '}
                          {row.visibility === 'clinic'
                            ? t('documents.highlights.shared_short')
                            : t('documents.highlights.private')}
                        </span>
                        <span className={cn(TYPE.small, 'text-ds-muted')}>
                          {row.filename}
                          {' · '}
                          {t('documents.highlights.pageGroup', { page: row.page })}
                        </span>
                      </button>
                      {canCite ? (
                        <div className="jw-mark-cite">
                          <Button
                            type="button"
                            variant="secondary"
                            data-testid={`journal-history-marking-cite-${row.id}`}
                            aria-label={`${t('journal.cite')} ${markingQuotePreview(row.quote)}`}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => citeMarking(row)}
                          >
                            <Quote className="h-4 w-4" strokeWidth={ICON} aria-hidden />
                            {t('journal.cite')}
                          </Button>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </>
  );
}
